/**
 * Controls Module
 * Handles UI interactions and parameter synchronization
 */

import { PhysicsInputs, PhysicsState } from '../simulation/physics';

// Parameters that can be adjusted by the user
interface ControlParameters extends PhysicsInputs {
  animate: boolean;
  speed: number;
}

type UpdateCallback = (params: ControlParameters) => void;

interface ControlElements {
  v0Value: HTMLElement;
  v0Slider: HTMLInputElement;
  vValue: HTMLElement;
  vSlider: HTMLInputElement;
  thetaValue: HTMLElement;
  thetaSlider: HTMLInputElement;
  bValue: HTMLElement;
  bSlider: HTMLInputElement;
  zoomValue: HTMLElement;
  zoomSlider: HTMLInputElement;
  animate: HTMLInputElement;
  speedValue: HTMLElement;
  speedSlider: HTMLInputElement;
}

interface ReadoutElements {
  U: HTMLElement;
  delta: HTMLElement;
  rp: HTMLElement;
  vin: HTMLElement;
  vout: HTMLElement;
  dE: HTMLElement;
}

export class Controls {
  private updateCallback: UpdateCallback;
  private elements: ControlElements;
  private readouts: ReadoutElements;

  // Jupiter parameters (fixed)
  private readonly JUPITER_MU = 126686534; // km³/s²
  private readonly JUPITER_RADIUS = 69911; // km

  constructor(updateCallback: UpdateCallback) {
    this.updateCallback = updateCallback;

    // Get all control elements
    this.elements = {
      v0Value: this.getElement('v0-value'),
      v0Slider: this.getElement<HTMLInputElement>('v0-slider'),
      vValue: this.getElement('v-value'),
      vSlider: this.getElement<HTMLInputElement>('v-slider'),
      thetaValue: this.getElement('theta-value'),
      thetaSlider: this.getElement<HTMLInputElement>('theta-slider'),
      bValue: this.getElement('b-value'),
      bSlider: this.getElement<HTMLInputElement>('b-slider'),
      zoomValue: this.getElement('zoom-value'),
      zoomSlider: this.getElement<HTMLInputElement>('zoom-slider'),
      animate: this.getElement<HTMLInputElement>('animate'),
      speedValue: this.getElement('speed-value'),
      speedSlider: this.getElement<HTMLInputElement>('speed-slider')
    };

    // Readout elements
    this.readouts = {
      U: this.getElement('U-value'),
      delta: this.getElement('delta-value'),
      rp: this.getElement('rp-value'),
      vin: this.getElement('vin-value'),
      vout: this.getElement('vout-value'),
      dE: this.getElement('dE-value')
    };

    this.setupEventListeners();
  }

  private getElement<T extends HTMLElement>(id: string): T {
    const element = document.getElementById(id);
    if (!element) {
      throw new Error(`Element with id "${id}" not found`);
    }
    return element as T;
  }

  /**
   * Format number with space as thousands separator
   */
  private formatWithSpaces(num: number, decimals: number = 0): string {
    const fixed = num.toFixed(decimals);
    const parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return parts.join('.');
  }

  /**
   * Set up all event listeners
   */
  private setupEventListeners(): void {
    // Sync sliders with display values
    this.syncControl('v0Value', 'v0Slider', (val) => `${val} km/s`);
    this.syncControl('vValue', 'vSlider', (val) => `${val} km/s`);
    this.syncControl('thetaValue', 'thetaSlider', (val) => `${val}°`);
    this.syncControl('bValue', 'bSlider', (val) => {
      const num = parseFloat(val);
      return `${this.formatWithSpaces(num, 0)} km`;
    });
    this.syncControl('zoomValue', 'zoomSlider', (val) => `${val}×`);
    this.syncControl('speedValue', 'speedSlider', (val) => `${val}×`);

    // Animation toggle
    this.elements.animate.addEventListener('change', () => this.update());
  }

  /**
   * Sync a slider with its display value
   */
  private syncControl(
    displayId: keyof ControlElements,
    sliderId: keyof ControlElements,
    formatter: (value: string) => string
  ): void {
    const display = this.elements[displayId] as HTMLElement;
    const slider = this.elements[sliderId] as HTMLInputElement;

    slider.addEventListener('input', () => {
      display.textContent = formatter(slider.value);
      this.update();
    });
  }

  /**
   * Get current parameter values
   */
  getParameters(): ControlParameters {
    return {
      mu: this.JUPITER_MU,
      v0: parseFloat(this.elements.v0Slider.value),
      v: parseFloat(this.elements.vSlider.value),
      theta: parseFloat(this.elements.thetaSlider.value),
      b: parseFloat(this.elements.bSlider.value),
      Rpl: this.JUPITER_RADIUS,
      zoom: parseFloat(this.elements.zoomSlider.value),
      animate: this.elements.animate.checked,
      speed: parseFloat(this.elements.speedSlider.value)
    };
  }

  /**
   * Update simulation
   */
  private update(): void {
    if (this.updateCallback) {
      this.updateCallback(this.getParameters());
    }
  }

  /**
   * Update numeric readouts
   */
  updateReadouts(state: PhysicsState | null): void {
    if (!state) {
      Object.values(this.readouts).forEach(el => el.textContent = '-');
      return;
    }

    this.readouts.U.textContent = `${this.formatWithSpaces(state.U, 1)} km/s`;
    this.readouts.delta.textContent = `${this.formatWithSpaces(state.delta * 180 / Math.PI, 1)}°`;
    this.readouts.rp.textContent = `${this.formatWithSpaces(state.rp, 0)} km`;
    this.readouts.vin.textContent = `${this.formatWithSpaces(state.v_in_mag, 1)} km/s`;
    this.readouts.vout.textContent = `${this.formatWithSpaces(state.v_out_mag, 1)} km/s`;
    this.readouts.dE.textContent = `${this.formatWithSpaces(state.deltaE, 0)} km²/s²`;
  }

  /**
   * Display status messages (warnings/errors)
   * Currently just logs to console as UI doesn't have status area
   */
  displayStatus(warnings: Array<{ type: string; msg: string }>): void {
    if (warnings && warnings.length > 0) {
      warnings.forEach(w => {
        if (w.type === 'error') {
          console.error(w.msg);
        } else if (w.type === 'warning') {
          console.warn(w.msg);
        } else {
          console.info(w.msg);
        }
      });
    }
  }

  /**
   * Initialize with default values
   */
  initialize(): void {
    // Set default values for Jupiter flyby scenario
    this.elements.v0Slider.value = '13';
    this.elements.v0Value.textContent = '13 km/s';

    this.elements.vSlider.value = '20';
    this.elements.vValue.textContent = '20 km/s';

    this.elements.thetaSlider.value = '180';
    this.elements.thetaValue.textContent = '180°';

    this.elements.bSlider.value = '200000';
    this.elements.bValue.textContent = `${this.formatWithSpaces(200000, 0)} km`;

    this.elements.zoomSlider.value = '1';
    this.elements.zoomValue.textContent = '1×';

    this.elements.speedSlider.value = '1';
    this.elements.speedValue.textContent = '1×';

    this.elements.animate.checked = false;

    this.update();
  }
}
